import { useCallback, useMemo } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { TeamsListDataDocument } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type {
  EntityBadgeConfig,
  EntityItemConfig,
  EntityPoolConfig,
  IconName,
} from "@salestrak/ui";
import { getEntityGlyph, type EntityData } from "#app/lib/entity-visuals";
import { useEntityDetailOptional } from "#app/components/entity";
import { useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { appendMissing, poolItemVisual } from "../utils";
import { useEntitySearchPool } from "./use-entity-search-pool";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "teams";
const INITIAL_LIMIT = 100;
const SEARCH_LIMIT = 50;
const INITIAL_QUERY_VARS = { limit: INITIAL_LIMIT };

type TeamNode = Omittable<{
  teamId: string;
  teamName: string;
  teamKey?: string | null;
  private?: boolean | null;
  icon?: { icon?: string | null; color?: string | null } | null;
}>;

// THE team → selector-item mapping (padded tinted rounded-square icon, kind
// fallback when no custom icon). Shared by every selector/filter listing teams.
export function teamToPoolItem(node: TeamNode): EntityItemConfig {
  const entity: EntityData = { kind: "team", id: node.teamId, team: node };
  // `render` drives the display; the primitive icon/color stay as data so
  // selections round-trip through selectionToIconEntity (and assignee team
  // detection keeps its avatar.icon fallback).
  const glyph = getEntityGlyph(entity);
  return {
    id: node.teamId,
    title: node.teamName,
    avatar: {
      icon: (glyph.icon ?? undefined) as IconName,
      color: glyph.color ?? undefined,
      padded: true,
      render: () => poolItemVisual(entity),
    },
    keywords: node.teamKey ? [node.teamKey] : undefined,
    data: {
      teamKey: node.teamKey,
      private: node.private,
      entityType: "team",
    },
  } satisfies EntityItemConfig;
}

export type UseTeamPoolOptions = Omittable<{
  /** Team IDs to exclude from the pool */
  exclude?: string[];
  /** The pool carries the hover "Team" row action (navigate to the team
   *  page) by DEFAULT — any selector popover listing teams offers it. Pass
   *  false only where the row grammar has no room for it (the mention
   *  typeahead). A list that passes its own `onItemAction` overrides the
   *  handler without needing this. */
  teamAction?: boolean;
}>;

/**
 * Fetches workspace teams and returns an EntityPoolConfig for use with EntitySelector.
 */
export function useTeamPool(options?: UseTeamPoolOptions) {
  const { exclude, teamAction = true } = options ?? {};
  const client = useApolloClient();
  const teamBadge = useTeamOpenBadge();
  const itemBadge = teamAction ? teamBadge : undefined;

  const { data, loading } = useQuery(TeamsListDataDocument, {
    variables: INITIAL_QUERY_VARS,
  });

  const initialItems = useMemo<EntityItemConfig[]>(() => {
    if (!data?.teams?.edges) return [];
    return data.teams.edges
      .filter((edge) => edge?.node)
      .map((edge) => teamToPoolItem(edge.node));
  }, [data]);

  const runSearch = useCallback(
    async (trimmed: string): Promise<EntityItemConfig[]> => {
      const { data: result } = await client.query({
        query: TeamsListDataDocument,
        variables: { limit: SEARCH_LIMIT, skip: 0, where: { search: trimmed } },
        fetchPolicy: "no-cache",
      });
      if (!result?.teams?.edges) return [];
      return result.teams.edges
        .filter((edge) => edge?.node)
        .map((edge) => teamToPoolItem(edge.node));
    },
    [client],
  );

  const { items, onSearchChange } = useEntitySearchPool({
    baseItems: initialItems,
    runSearch,
    exclude,
    // Stable epoch: the teams query has no filter variants, so cached server-
    // search results never need resetting (and must not be wiped by `User`/team
    // cache re-emission churning the base `data` reference mid-search).
    epoch: INITIAL_QUERY_VARS,
  });

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Teams",
      items,
      itemBadge,
      onSearchChange,
      // Server-filtered already; client-filtering would hide server matches beyond the first page.
      serverFiltered: true,
    }),
    [items, itemBadge, onSearchChange],
  );

  // `hasAny` and `baseItems` read the UNFILTERED base list — `pool.items`
  // reflects the active search, so neither an empty search result nor a
  // narrowed one may stand in for the workspace's teams.
  return {
    pool,
    loading,
    baseItems: initialItems,
    hasAny: initialItems.length > 0,
  };
}

export { POOL_ID as TEAM_POOL_ID };

/**
 * THE "Team" row-action badge for pools listing teams — the hover button on
 * picker rows that navigates to the team page. `useTeamPool` bakes it in; a
 * hand-built team pool (a filter chip, the members-table Teams cell) spreads
 * it as its `itemBadge`. Undefined without an entity-detail provider and
 * for a share-only viewer, whom the team page turns away.
 * `openEntity` refuses a team outside the viewer's loaded list — the
 * permission gate the team page itself applies.
 *
 * closeOnAction: the action navigates — a picker left open behind the
 * navigation reads as an unanswered question.
 */
export function useTeamOpenBadge(): EntityBadgeConfig | undefined {
  const detail = useEntityDetailOptional();
  const shareOnly = useViewerIsShareOnly();
  const openTeam = useCallback(
    (entity: EntityItemConfig) => {
      detail?.openEntity("team", entity.id);
    },
    [detail],
  );
  // A share-only viewer (Guest, Limited Member) gets no "Team" row action:
  // the team page is a member module (`RequireMemberModule`) that answers
  // Access Denied for them, so the badge would lead nowhere. They still
  // assign the team; they just cannot browse it.
  // Nor inside a contained surface (an unsaved draft), which navigating would discard.
  const available = detail != null && !detail.contained && !shareOnly;
  return useMemo(
    () =>
      available
        ? {
            closeOnAction: true,
            label: "Team",
            tooltip: "Open Team",
            onAction: openTeam,
          }
        : undefined,
    [available, openTeam],
  );
}

/**
 * `items` plus each of `ids` it does not hold, fetched by id — for lists
 * whose groups come from a server count (team bands, board columns): the
 * team pool loads one page, so a team past it would otherwise get no group.
 * Ids that are not teams (the unassigned bucket) match nothing.
 */
export function useTeamItemsWith(
  items: EntityItemConfig[],
  ids: readonly string[],
): EntityItemConfig[] {
  const { teamMap } = useWorkspaceData();
  const unloaded = useMemo(() => {
    const known = new Set(items.map((item) => item.id));
    return ids.filter((id) => !known.has(id) && !teamMap.has(id)).sort();
  }, [items, ids, teamMap]);
  const variables = useMemo(
    () => ({ where: { teamIds: { hasSome: unloaded } }, limit: unloaded.length }),
    [unloaded],
  );
  const { data, previousData } = useQuery(TeamsListDataDocument, {
    variables,
    skip: unloaded.length === 0,
  });
  return useMemo(() => {
    const fetched = new Map(
      ((data ?? previousData)?.teams?.edges ?? [])
        .filter((edge) => edge?.node)
        .map((edge) => [edge.node.teamId, teamToPoolItem(edge.node)]),
    );
    return appendMissing(items, ids, (id) => {
      const team = teamMap.get(id);
      return team ? teamToPoolItem(team) : fetched.get(id);
    });
  }, [items, ids, teamMap, data, previousData]);
}
